import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { GroupService } from '@klickdata/core/group';
import { User, UserService } from '@klickdata/core/user';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    switchMap,
    takeUntil,
} from 'rxjs';

@Component({
    selector: 'app-user-search-chips-display',
    templateUrl: './user-search-chips-display.component.html',
    styleUrls: ['./user-search-chips-display.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchChipsDisplayComponent extends OnDestroyHandler implements OnInit, AfterViewInit {
    @Input() user: User;
    public participantUsers: User[] = [];
    public userSearch$: Observable<User[]>;
    public userCtrl = new FormControl();
    @Output() onUpdateSelectiedUsers: EventEmitter<User[]> = new EventEmitter();
    @ViewChild('userRecieverInput') userRecieverInput: ElementRef<HTMLInputElement>;
    constructor(
        protected groupService: GroupService,
        protected userService: UserService,
        protected cd: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {
        if (!!this.user.group_id) {
            this.groupService
                .getGroup(this.user.group_id, 'users')
                .pipe(
                    takeUntil(this.destroy),
                    filter((group) => !!group?.users)
                )
                .subscribe((group) => {
                    this.participantUsers = group.users as unknown as User[];
                    this.notifyUpdate();
                });
        } else {
            this.participantUsers = [this.user];
            this.notifyUpdate();
        }
    }
    ngAfterViewInit(): void {
        this.userSearch$ = combineLatest([
            this.userCtrl.valueChanges.pipe(
                filter((term) => typeof term === 'string'),
                debounceTime(300),
                distinctUntilChanged()
            ),
            of(this.user),
        ]).pipe(
            switchMap(([term, user]) =>
                combineLatest([
                    of(user),
                    term.trim()
                        ? this.userService.getCustomerLightUsers({ customerId: user.customer_id, term: term })
                        : of(<User[]>[]),
                ])
            ),
            map(([ownUser, users]) =>
                users.filter(
                    (user) =>
                        this.participantUsers.findIndex((recipient) => recipient.id === user.id) === -1 &&
                        user.id !== ownUser.id
                )
            ),
            takeUntil(this.destroy)
        );
    }
    public selected(auto: MatAutocomplete, event: MatAutocompleteSelectedEvent): void {
        this.participantUsers.push(event.option.value);
        this.userRecieverInput.nativeElement.value = '';
        this.userCtrl.setValue('');
        auto.options.reset([]);
        this.notifyUpdate();
    }
    public removeUser(user: User): void {
        const index = this.participantUsers.indexOf(user);
        if (index >= 0) {
            this.participantUsers.splice(index, 1);
            this.notifyUpdate();
        }
    }
    private notifyUpdate() {
        this.onUpdateSelectiedUsers.emit(this.participantUsers);
        this.cd.markForCheck();
    }
}
